import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { ApolloConsumer } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  useApolloContext,
  useLocationsContext,
  useToast,
  useUserContext,
} from '../../contexts';
import { Dropdown, DropdownProps } from '@fluentui/react-northstar';
import {
  classifyOrganizationSwitchError,
  switchOrganization,
} from '../../services/teamsBot';
import { logError } from '../../utilities/ErrorLogger';

type OrgDropdownItem = {
  key: number;
  header: string;
  avatar: string;
};

export const OrganizationDropdown = () => {
  const { currentOrgs, azureAdId } = useUserContext();
  const { tenantId, setTenantId, setJwt } = useApolloContext();
  const { refetch } = useLocationsContext();
  const toast = useToast();
  const { t } = useTranslation('weekSchedule');
  const [switching, setSwitching] = useState(false);

  const items: OrgDropdownItem[] =
    currentOrgs?.map(({ id, name, avatar }) => ({
      key: Number(id),
      header: name,
      avatar,
    })) || [];

  // The API rejects a Tenant-Id that differs from the org the access token is
  // bound to, so the org only changes together with a token issued for it. If
  // that token cannot be obtained, the current org stays and the user is told.
  const handleOrganizationChange = async (
    _event: React.MouseEvent | React.KeyboardEvent | null,
    data: DropdownProps
  ) => {
    const { key } = data.value as OrgDropdownItem;
    const organizationId = key.toString();

    if (switching || organizationId === tenantId) {
      return;
    }
    if (!azureAdId) {
      toast.error(t('organization_switch.failed'));
      return;
    }

    setSwitching(true);
    try {
      const { token } = await switchOrganization(azureAdId, organizationId);

      // Applied in one render so no request goes out with the new token and
      // the old tenant, or the reverse.
      unstable_batchedUpdates(() => {
        setJwt(token.access_token);
        setTenantId(organizationId);
      });
      refetch();
    } catch (e) {
      const failure = await classifyOrganizationSwitchError(e);
      if (failure === 'failed') {
        logError(
          e instanceof Error ? e : new Error(`Organization switch failed: ${e}`)
        );
      }
      toast.error(t(`organization_switch.${failure}`));
    } finally {
      setSwitching(false);
    }
  };

  if (!currentOrgs?.length || currentOrgs?.length <= 1) {
    return null;
  }

  return (
    <ApolloConsumer>
      {(client) => (
        <Dropdown
          items={items}
          value={items.find((i) => i.key === Number(tenantId))?.header}
          onChange={handleOrganizationChange}
          disabled={switching}
          onClick={() => client.resetStore()}
          highlightedIndex={items.findIndex((i) => i.key === Number(tenantId))}
          fluid
          inverted
        />
      )}
    </ApolloConsumer>
  );
};
